import { ROUTER_ON_LOCATION_CHANGED } from '@lagunovsky/redux-react-router'
import { call, fork, put, race, select, take, takeLatest } from 'redux-saga/effects'

import { PATH_NAME } from 'app/constants'
import { findCinemaId } from 'global/helpers'
import { cinemaSlugToId, normalizeCinemaId } from 'global/helpers/cinemaList'
import { getCinemaSlug } from 'pages/Hall/helpers'
import { confirmCinemaSelect, finishCinemaSelect, openCinemaSelect } from './reducer'

// Keep the caller (and its form payload) suspended until the user chooses a cinema.
export function* requireCinemaId(preferredCinemaId) {
    const cinemaid = normalizeCinemaId(preferredCinemaId) ?? (yield select(findCinemaId))
    if (cinemaid) return cinemaid

    try {
        yield put(openCinemaSelect())
        const { confirmed } = yield race({
            confirmed: take((action) => confirmCinemaSelect.match(action) && !!normalizeCinemaId(action.payload)),
            navigated: take(ROUTER_ON_LOCATION_CHANGED),
        })
        return confirmed ? normalizeCinemaId(confirmed.payload) : null
    } finally {
        // Replacing one caller must not dismiss other operations sharing the dialog.
        yield put(finishCinemaSelect())
    }
}

// These routes render AppLayout without its dialog, or bypass AppLayout altogether.
const routesWithoutCinemaDialog = new Set([
    PATH_NAME.ERROR,
    PATH_NAME.NOT_FOUND,
    PATH_NAME.VERIFY_PAYMENT,
    PATH_NAME.Maintenance,
    PATH_NAME.HEALTHCHECK,
    PATH_NAME.EVERYTHING_ELSE,
])
const cinemaRoutes = new Set(Object.values(PATH_NAME).filter((path) => !routesWithoutCinemaDialog.has(path)))

function* ensureRouteCinema({ payload }) {
    const location = payload?.location
    const pathname = location?.pathname?.toLowerCase().replace(/\/+$/, '') || '/'
    if (!location || !cinemaRoutes.has(pathname)) return

    const params = new URLSearchParams(location.search)
    const cinemaid =
        cinemaSlugToId(params.get('site')) ??
        (pathname === PATH_NAME.THANKS_FOR_ORDER ? normalizeCinemaId(params.get('transactionCinemaId')) : null)
    if (cinemaid) {
        localStorage.setItem('cinemaid', cinemaid)
        const site = getCinemaSlug(cinemaid)
        if (site) localStorage.setItem('site', site)
    }

    yield call(requireCinemaId, cinemaid)
}

export default function* cinemaSelectionSaga() {
    yield takeLatest(ROUTER_ON_LOCATION_CHANGED, ensureRouteCinema)
    // ReduxRouter does not emit a location-change action for an unchanged initial URL.
    const location = yield select((state) => state.navigator?.location)
    yield fork(ensureRouteCinema, { payload: { location } })
}
